/**
 * Shared prompt snippets describing the requirements taxonomy.
 *
 * The 11-category taxonomy (BR / FR / NFR / TR / ADR / SEC / DR / INT /
 * OPS / UX / MT), the four-tier architectural classification (Platform,
 * Industry, Customer Workflow, Consumer Application), and the
 * category-disambiguation rules (SEC vs NFR, TR vs ADR, FR vs INT, DR
 * vs SEC, NFR vs OPS) are consumed by multiple agent bundles. To keep
 * the writer, reviewer, and analyst bundles from drifting apart, all
 * three import these constants instead of restating the tables and
 * rules in their own prompt strings.
 *
 * Each export is a `ReadonlyArray<string>` so it can be spread directly
 * into a sub-agent `prompt` array. The snippets intentionally do **not**
 * include leading or trailing section dividers (`---`) — the consuming
 * bundle decides where these sections fit into its own prompt outline.
 *
 * See issue #408 for the consolidation rationale.
 */

/**
 * The 11-category taxonomy table. Introduces the category grid used by
 * every requirement document and states that directory names are
 * defaults while category prefixes are universal.
 *
 * Consumers: `requirements-writer` and `requirements-reviewer` embed
 * this verbatim. The analyst references the table indirectly through
 * its "respect the taxonomy" note.
 */
export const REQUIREMENTS_TAXONOMY_TABLE_SECTION: ReadonlyArray<string> = [
  "## The 11-Category Taxonomy",
  "",
  "All requirements follow the BIZBOK-aligned 11-category taxonomy",
  "below. Getting requirements into the right category matters because",
  "it determines which template applies, which standards inform it, and",
  "how it traces to other requirements.",
  "",
  "| Category | Prefix | Directory (default) | Question it answers |",
  "|----------|--------|---------------------|---------------------|",
  "| Business Requirements | BR | `business/` | **Why** are we building this? What business value does it deliver? |",
  "| Functional Requirements | FR | `functional/` | **What** does the system do? What is the user-visible behavior? |",
  "| Non-Functional Requirements | NFR | `non-functional/` | **How well** must the system perform? What quality bar must it meet? |",
  "| Technical Requirements | TR | `technical/` | **What technology** are we using, and why that one? |",
  "| Architectural Decisions | ADR | `architectural-decisions/` | **What structural decision** are we making, and what are the trade-offs? |",
  "| Security & Compliance | SEC | `security/` | **What must we protect**, and what rules govern that protection? |",
  "| Data Requirements | DR | `data/` | **What data** do we store, how long, and how do we recover it? |",
  "| Integration Requirements | INT | `integration/` | **What external systems** do we connect to, and how? |",
  "| Operational Requirements | OPS | `operational/` | **How do we deploy, monitor, and respond** to incidents? |",
  "| UX Requirements | UX | `ux/` | **How must the interface look, feel, and behave** for users? |",
  "| Multi-Tenancy & Licensing | MT | `multi-tenancy/` | **How do we isolate tenants, gate features, and meter usage?** |",
  "",
  "All requirement documents live under `<REQUIREMENTS_ROOT>` in the",
  "category directory shown above. Directory names are defaults —",
  "consuming projects may rename them, but the prefixes are universal.",
];

/**
 * The full disambiguation rule bullets. Used when deciding which
 * category a requirement belongs to. Both the writer and the reviewer
 * (in its Categorization Accuracy check) apply the same rules to avoid
 * drift.
 *
 * The constant intentionally ships the bullets **without** a heading —
 * the writer wraps them in an `### Disambiguation Rules` section under
 * the 11-category taxonomy, while the reviewer threads them directly
 * into its Check 2 (Categorization Accuracy) prose. Keeping the heading
 * out of the shared snippet lets each consumer choose the right
 * heading level for its surrounding outline.
 */
export const REQUIREMENTS_TAXONOMY_DISAMBIGUATION_SECTION: ReadonlyArray<string> =
  [
    "- **SEC vs NFR:** Protecting data, enforcing access control, or",
    "  meeting a regulation → SEC. System performance, uptime, or",
    '  scalability → NFR. "99.9% uptime" is NFR; "audit logs for all',
    '  admin actions" is SEC.',
    '- **TR vs ADR:** A TR pins down a specific technology choice ("use',
    '  PostgreSQL 16+"). An ADR records a structural decision with',
    '  context, trade-offs, and alternatives ("why row-level isolation',
    '  over schema-per-tenant"). TRs often follow from ADRs.',
    "- **FR vs INT:** What the user experiences → FR. How two systems",
    '  communicate → INT. "User can pay with a credit card" is FR;',
    '  "Stripe Checkout API integration for payment processing" is INT.',
    "- **DR vs SEC:** What data exists, how long we keep it, and how we",
    "  recover it → DR. Who can access it and how it's protected → SEC.",
    "  These often cross-reference each other.",
    "- **NFR vs OPS:** A measurable target the system must meet → NFR.",
    '  The tooling and processes to run the system → OPS. "p99 latency',
    '  < 200ms" is NFR; "Datadog APM for latency monitoring" is OPS.',
  ];

/**
 * The four architectural tiers. Orthogonal to category — category
 * answers "what kind" while tier answers "where in the architecture."
 *
 * Only the tier **table** lives here. The writer bundle owns the
 * prose that explains tier metadata fields (Customer, Implementor),
 * cross-tier traceability, and the `tier:*` issue labels — those are
 * writer-specific concerns that do not belong in the shared reference.
 */
export const REQUIREMENTS_TIER_TABLE_SECTION: ReadonlyArray<string> = [
  "| Tier | Slug | When to use |",
  "|------|------|-------------|",
  "| **Platform** | `platform` | Core platform services — APIs, tenant isolation, auth, event bus, shared infrastructure |",
  "| **Industry** | `industry` | Vertical-specific capabilities not every tenant needs |",
  "| **Customer Workflow** | `customer-workflow` | Business logic tenants configure within the platform — approval chains, notification rules, intake processes |",
  "| **Consumer Application** | `consumer-app` | UI/UX and integrations in external front-ends/systems consuming the platform's APIs |",
];

/**
 * The `req:write` issue body schema. Every bundle that opens a
 * `req:write` issue (the analyst's trace phase, the reviewer's review
 * and deprecation phases) **and** the writer bundle's intake step
 * embed this section verbatim so the three producers and the single
 * consumer never drift apart. The schema documents the three required
 * fields the writer parses, the one strongly-recommended
 * `Inputs / Read` field, and the `Missing:` escape hatch that upstream
 * producers use when a required field cannot be derived.
 *
 * Consumers embed the section under their own heading — the heading
 * is intentionally omitted here so each bundle can choose the right
 * outline level for its surrounding context.
 *
 * See issue #411 for the consolidation rationale.
 */
export const REQ_WRITE_ISSUE_SCHEMA_SECTION: ReadonlyArray<string> = [
  "The body of every `req:write` issue **must** declare the three",
  "fields below. The downstream `requirements-writer` agent parses",
  "them on intake and will refuse to write a document (and flag the",
  "issue `status:needs-attention`) if any required field is missing",
  "or contradictory.",
  "",
  "### Required Fields",
  "",
  "- **Category:** one of `BR` / `FR` / `NFR` / `TR` / `ADR` / `SEC` /",
  "  `DR` / `INT` / `OPS` / `UX` / `MT`. Must match the category",
  "  prefix in the requirement's title and file name.",
  "- **Tier:** one of `platform` / `industry` / `customer-workflow` /",
  "  `consumer-app`. Must match the `tier:*` label applied to the",
  "  issue.",
  "- **Output Path:** absolute-from-repo-root path ending in",
  "  `<PREFIX>-<NNN>-<slug>.md` under the target category directory",
  "  (for example,",
  "  `docs/src/content/docs/requirements/functional/FR-012-checkout-flow.md`).",
  "",
  "### Strongly Recommended",
  "",
  "- **Requirement ID:** the reserved requirement ID (e.g.",
  "  `FR-1240`), a HINT allocated at issue-creation. Optional —",
  "  legacy issues may omit it. The writer re-validates and finalizes",
  "  the ID at write-start (via `next-requirement-id.sh`) before",
  "  authoring, so this field is advisory: if a concurrent claim took",
  "  the reserved number, the writer authors under the freshly",
  "  finalized ID instead and updates this field to match.",
  "- **Inputs / Read:** bullet list of source files the writer should",
  "  consult — the upstream proposal file (when one exists), the",
  "  BCM/source document(s) the proposal cites, and any related",
  "  requirements the new document should trace to. Omit only for",
  "  ad-hoc requirements where no upstream proposal exists; the",
  "  writer will then treat the issue body itself as the input.",
  "",
  "### Missing Fields",
  "",
  "When an upstream producer cannot derive one of the required",
  "fields, it **must not** guess a value. Instead it opens the",
  "`req:write` issue with `status:needs-attention` (not",
  "`status:ready`) and includes a `Missing:` line listing the",
  "field(s) that could not be determined. For example:",
  "",
  "```",
  "Missing: Tier — source document has no tier metadata and the",
  "referencing requirement is cross-tier.",
  "```",
  "",
  "A human triages the issue, fills in the missing field, and flips",
  "the label to `status:ready` before the writer picks it up.",
];
